import { Box, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import ExportButton from '~/components/Buttons/ExportButton';
import DoseItem from '~/components/DoseItem/DoseItem';
import ImageItem from '~/components/ImageItem/ImageItem';
import { RegistrationRow } from '~/components/RegistrationRow/RegistrationRows';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';
import StructureSetItem from '~/components/Structures/StructureSetItem';
import { Modality } from '~/domain/dicom/modality';
import { createAvailableStructureSetViewFromDomain } from '~/domain/projections/factories';
import useExportableItems from '~/hooks/export/useExportableItems';
import usePrimaryImage from '~/hooks/image/usePrimaryImage';
import useConfirmedSelection from '~/hooks/datasetSelection/useConfirmedSelection';
import {
    useActivateRegistration,
    useRegistrationState,
} from '~/hooks/registration/useRegistrationStore';
import { currentStructureSetIdState } from '~/state/atoms/structureSet/structureSetAtoms';
import { exportDatasetsModalOpenState } from '~/state/atoms/viewerAtoms';
import StepHeader from '../StepHeader';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { getDoseLabel } from '../DoseStep/doseUtils';

export default function ExportContent() {
    const setExportModalOpen = useSetRecoilState(exportDatasetsModalOpenState);
    const intl = useIntl();
    const {
        images: exportableImages,
        structureSets: exportableStructureSets,
        doses: exportableDoses,
        registrations: exportableRegistrations,
        notExportedCount,
    } = useExportableItems();
    const { primaryImage, handleChangePrimaryImage } = usePrimaryImage();
    const currentDose = useCurrentDose();
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const { pickDose, pickStructureSet } = useConfirmedSelection();
    const activateRegistration = useActivateRegistration();
    const registrationState = useRegistrationState();

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box flex={1} overflow="auto" minHeight={0}>
                <StepHeader
                    titleId="workflow.export"
                    descriptionId="workflow.export.description"
                />
                <Box px={2} py={1}>
                    <ExportButton
                        notExportedItemsCount={notExportedCount}
                        onOpenExportModal={() => setExportModalOpen(true)}
                    />
                </Box>

                {exportableImages.length > 0 && (
                    <Box px={2} pt={1.5} pb={1}>
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="images" />
                        </Typography>
                    </Box>
                )}
                {exportableImages.map((img) => (
                    <Box
                        key={img.dicomUid}
                        borderTop="1px solid"
                        borderColor="divider"
                    >
                        <ImageItem
                            image={img}
                            isSelected={
                                primaryImage?.seriesUid === img.seriesUid
                            }
                            onSelect={() =>
                                handleChangePrimaryImage({
                                    seriesInstanceUID: img.seriesUid,
                                    studyInstanceUID: img.studyUid,
                                    label:
                                        img.seriesMetadata.seriesDescription ||
                                        intl.formatMessage({
                                            id: 'unknown.series',
                                        }),
                                    modality: img.seriesMetadata
                                        .modality as Modality,
                                    isLoaded: true,
                                    imageObject: img,
                                })
                            }
                        />
                    </Box>
                ))}

                {exportableStructureSets.length > 0 && (
                    <Box px={2} pt={1.5} pb={1}>
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="structureSets" />
                        </Typography>
                    </Box>
                )}
                {exportableStructureSets.map((ss) => {
                    const base = createAvailableStructureSetViewFromDomain(ss);
                    // The factory uses structureSetLabel verbatim; preserve
                    // the legacy intl fallback for SSes with a blank label.
                    const available = {
                        ...base,
                        displayName:
                            base.displayName ||
                            intl.formatMessage({ id: 'structureSet' }),
                    };
                    return (
                        <Box
                            key={ss.dicomUid}
                            borderTop="1px solid"
                            borderColor="divider"
                        >
                            <StructureSetItem
                                structureSet={available}
                                isActive={currentStructureSetId === ss.dicomUid}
                                allowReselect
                                onClick={() => pickStructureSet(available)}
                            />
                        </Box>
                    );
                })}

                {exportableDoses.length > 0 && (
                    <Box px={2} pt={1.5} pb={1}>
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="doses" />
                        </Typography>
                    </Box>
                )}
                {exportableDoses.map((dose) => (
                    <Box
                        key={dose.dicomUid}
                        borderTop="1px solid"
                        borderColor="divider"
                    >
                        <SelectableListItem
                            isSelected={currentDose?.dicomUid === dose.dicomUid}
                            onClick={() => pickDose(dose)}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <DoseItem
                                dose={dose}
                                label={getDoseLabel(
                                    dose.plan?.label,
                                    dose.doseSummationType
                                )}
                            />
                        </SelectableListItem>
                    </Box>
                ))}

                {exportableRegistrations.length > 0 && (
                    <Box px={2} pt={1.5} pb={1}>
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="registrations" />
                        </Typography>
                    </Box>
                )}
                <Box display="flex" flexDirection="column" px={1.5}>
                    {exportableRegistrations.map((reg) => {
                        const secondaryImage = exportableImages.find(
                            (img) =>
                                img.frameOfReferenceUid ===
                                reg.targetFrameOfReferenceUid
                        );
                        const sourceImage = exportableImages.find(
                            (img) =>
                                img.frameOfReferenceUid ===
                                reg.sourceFrameOfReferenceUid
                        );
                        const canActivate = !!secondaryImage && !!sourceImage;
                        return (
                            <RegistrationRow
                                key={reg.dicomUid}
                                label={reg.contentLabel || reg.dicomUid}
                                approvalStatus={reg.approvalStatus}
                                selected={
                                    registrationState.selectedSopInstanceUid ===
                                    reg.dicomUid
                                }
                                onClick={
                                    canActivate
                                        ? async () => {
                                              // A refused switch fuses
                                              // nothing: the pair belongs
                                              // to the source image.
                                              const switched =
                                                  await handleChangePrimaryImage(
                                                      {
                                                          seriesInstanceUID:
                                                              sourceImage.seriesUid,
                                                          studyInstanceUID:
                                                              sourceImage.studyUid,
                                                          label:
                                                              sourceImage
                                                                  .seriesMetadata
                                                                  .seriesDescription ||
                                                              intl.formatMessage(
                                                                  {
                                                                      id: 'unknown.series',
                                                                  }
                                                              ),
                                                          modality: sourceImage
                                                              .seriesMetadata
                                                              .modality as Modality,
                                                          isLoaded: true,
                                                          imageObject:
                                                              sourceImage,
                                                      }
                                                  );
                                              if (!switched) {
                                                  return;
                                              }
                                              activateRegistration(
                                                  reg.dicomUid,
                                                  secondaryImage.seriesUid,
                                                  sourceImage
                                              );
                                          }
                                        : undefined
                                }
                                details={{ registration: reg }}
                            />
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
